import React, { useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import ServiceProviderAvailabilityForm from './ServiceProviderAvailabilityForm';
import { closeServiceProviderAvailabilitiesDialog, selectServiceProviderAvailabilitiesDialog } from '../../stateManagement/serviceProviderAvailabilitiesDialogSlice';
import { deleteServiceProviderAvailabilityAsync, fetchServiceProviderAvailabilitys, selectServiceProviderAvailabilitys, selectServiceProviderAvailabilitysLoading } from '../../stateManagement/serviceProviderAvailabilitySlice';
import { AppDispatch } from '../../stateManagement/store';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { useTheme, useMediaQuery } from '@mui/material';
import { IServiceProviderAvailability } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';

const ServiceProviderAvailabilityListDialog: React.FC = () => {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const serviceProviderAvailabilitiesDialog = useSelector(selectServiceProviderAvailabilitiesDialog);

    const selectedOrganization = useSelector(selectSelectedOrganization); // ✅ Get orgId from state
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);
    const serviceProviderAvailabilitys = useSelector(selectServiceProviderAvailabilitys);
    const loading = useSelector(selectServiceProviderAvailabilitysLoading);

    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const onClickDeleteFolder = () => {
        setShowDeleted(!showDeleted);
    }

    const dispatch = useDispatch<AppDispatch>();

    const [formOpen, setFormOpen] = useState<{ open: boolean; serviceProvider?: IServiceProviderAvailability | null }>({ open: false });

    const closeForm = () => {
        setFormOpen({ open: false });
    };

    const [rowLoading, setRowLoading] = useState<string[]>([]);

    const handleDelete = async (input: IServiceProviderAvailability) => {
        if (input.organizationId) {
            setRowLoading([...rowLoading, input.serviceProviderAvailabilityId]);
            dispatch(deleteServiceProviderAvailabilityAsync({
                organizationId: input?.organizationId ?? "",
                serviceProviderId: selectedServiceProvider.serviceProviderId ?? '',
                serviceProviderAvailabilityId: input.serviceProviderAvailabilityId,
                currentIsDeleted: input.isDeleted ?? false,
            }))
                .unwrap()
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setRowLoading(rowLoading.filter((item) => item !== input.serviceProviderAvailabilityId))
                });
        }
    };

    const serviceProviderAvailabilityType: GridColumnType<IServiceProviderAvailability> = {
        serviceProviderAvailabilityId: { type: "Id", dontShow: true },
        organizationId: { type: "Integer", dontShow: true },
        serviceProviderId: { type: "Integer", dontShow: true },
        startDate: { type: "Date", header: 'Start Date', },
        endDate: { type: "Date", header: 'End Date' },
        serviceProviderAvailabilityDays: { type: "List", dontShow: true },
    };

    const handleNew = () => {
        setFormOpen({ open: true })
    };

    const handleEdit = (input: IServiceProviderAvailability) => {
        setFormOpen({ open: true, serviceProvider: input });
    };

    const handleClose = () => {
        setShowDeleted(false);
        dispatch(closeServiceProviderAvailabilitiesDialog());
    };

    useEffect(() => {
        if (selectedOrganization.organizationId && selectedServiceProvider.serviceProviderId) {

            dispatch(fetchServiceProviderAvailabilitys({
                organizationId: selectedOrganization.organizationId,
                serviceProviderId: selectedServiceProvider.serviceProviderId,
                showDeleted
            }));
        }
    }, [selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, showDeleted]);

    return (
        <Dialog
            open={serviceProviderAvailabilitiesDialog.open}
            onClose={() => handleClose()}
            fullWidth
            fullScreen={fullScreen}
        >
            {formOpen.open ?
                <ServiceProviderAvailabilityForm serviceProviderInput={formOpen.serviceProvider} closeForm={closeForm} organizationId={serviceProviderAvailabilitiesDialog.organizationId} serviceProviderId={serviceProviderAvailabilitiesDialog.serviceProviderId} />
                :
                <GridTable
                    gridTitle={`${serviceProviderAvailabilitiesDialog.serviceProviderName} Availabilities `}
                    gridTitleInfo={`Manage availability periods for ${serviceProviderAvailabilitiesDialog.serviceProviderName} (the selected service provider)`}
                    loading={loading}
                    list={serviceProviderAvailabilitys.map((item) => ({ ...item, id: item.serviceProviderAvailabilityId }))}
                    inputType={serviceProviderAvailabilityType}
                    onClickNew={handleNew}
                    onEditRow={handleEdit}
                    onDeleteRow={handleDelete}
                    deleteLoading={rowLoading}
                    onClickBack={handleClose}
                    isOnDialog={true}
                    showInfoToggleButton={true}
                    onClickDeleteFolder={onClickDeleteFolder}
                    showDeleted={showDeleted}
                />
            }
        </Dialog>
    );
};

export default ServiceProviderAvailabilityListDialog;
